import { HEADER_BYTES, MALFORMED, OK, assertField, checkHeader, putHeader, putU16, putU32, readU16, readU32, type DecodeVerdict } from "./binary.ts";
import { MessageType } from "./catalogue.ts";
import { MAX_U16, MAX_U32 } from "./limits.ts";

/**
 * The `partyPositions` frame (ADR-100 decision 7, PROTOCOL.md 5.20): server to client, every 40 ticks, the ground points
 * of the receiver's party members its map server holds on its map. After the header: a `u32` tick, a `u8` count of 0
 * to 5, then 5 bytes a member, a `u8` place in the party roster (0 to 5, ADR-101 decision 1) and a `u16` x and `u16` y
 * in sixteenths. A map server writes it into a buffer it makes once a session (`putPartyPositionEntry`,
 * `putPartyPositionsHeader`), so a send allocates nothing; the decoder fills a record made once, allocates nothing,
 * never throws and never reads past the frame (ADR-030).
 */

/**
 * Bytes before the first member: the frame header, the tick and the count.
 */
export const PARTY_POSITIONS_HEADER_BYTES = HEADER_BYTES + 5;

/**
 * Bytes of one member: its place and its ground point.
 */
export const PARTY_POSITION_ENTRY_BYTES = 5;

/**
 * The most members one frame carries: a party of 6 less the receiver.
 */
export const MAX_PARTY_POSITIONS = 5;

/**
 * The highest place in a party roster (ADR-101 decision 1: places 0 to 5).
 */
export const MAX_PARTY_PLACE = 5;

const COUNT_OFFSET = HEADER_BYTES + 4;

/**
 * One member to encode: its place in the party roster and its ground point in sixteenths.
 */
export interface PartyPosition {
  readonly place: number;
  readonly x: number;
  readonly y: number;
}

/**
 * A decoded `partyPositions`, reused across frames: the tick, how many members the frame held, and their places and
 * ground points, the first `count` of each valid.
 */
export interface PartyPositionsRecord {
  tick: number;
  count: number;
  readonly places: Uint8Array;
  readonly xs: Uint16Array;
  readonly ys: Uint16Array;
}

/**
 * A fresh record for `decodePartyPositions` to fill, with room for the most members a frame carries.
 *
 * @returns An empty record.
 */
export function newPartyPositionsRecord(): PartyPositionsRecord {
  return { tick: 0, count: 0, places: new Uint8Array(MAX_PARTY_POSITIONS), xs: new Uint16Array(MAX_PARTY_POSITIONS), ys: new Uint16Array(MAX_PARTY_POSITIONS) };
}

/**
 * Bytes of a frame of a number of members.
 *
 * @param count The members.
 * @returns `8 + 5 * count`.
 */
export function partyPositionsBytes(count: number): number {
  return PARTY_POSITIONS_HEADER_BYTES + PARTY_POSITION_ENTRY_BYTES * count;
}

/**
 * Writes one member at its place in a frame through plain byte stores. The caller has sized the buffer and checked the
 * fields' widths, as a map server does with positions it holds in typed arrays of that width.
 *
 * @param buffer The frame.
 * @param index The member's place in the frame, from 0.
 * @param place Its place in the party roster.
 * @param x Its ground point's x, in sixteenths.
 * @param y Its ground point's y, in sixteenths.
 */
export function putPartyPositionEntry(buffer: Uint8Array, index: number, place: number, x: number, y: number): void {
  const at = PARTY_POSITIONS_HEADER_BYTES + PARTY_POSITION_ENTRY_BYTES * index;
  buffer[at] = place;
  putU16(buffer, at + 1, x);
  putU16(buffer, at + 3, y);
}

/**
 * Writes a frame's header once its members are in place.
 *
 * @param buffer The frame.
 * @param tick The server tick, a `u32`.
 * @param count The members written, 0 to 5.
 * @returns The frame's length in bytes.
 */
export function putPartyPositionsHeader(buffer: Uint8Array, tick: number, count: number): number {
  putHeader(buffer, MessageType.PartyPositions);
  putU32(buffer, HEADER_BYTES, tick);
  buffer[COUNT_OFFSET] = count;
  return partyPositionsBytes(count);
}

/**
 * Encodes a `partyPositions` into a caller-owned buffer, checking every field.
 *
 * @param buffer Destination, at least `partyPositionsBytes(members.length)` long.
 * @param tick The server tick.
 * @param members 0 to 5 members.
 * @returns Bytes written.
 * @throws Error when there are more than 5 members, the buffer is too small, a place is past 5 or a field does not fit
 *   its width.
 */
export function encodePartyPositions(buffer: Uint8Array, tick: number, members: readonly PartyPosition[]): number {
  if (members.length > MAX_PARTY_POSITIONS) {
    throw new Error(`partyPositions carries 0 to ${MAX_PARTY_POSITIONS} members, not ${members.length}`);
  }
  const length = partyPositionsBytes(members.length);
  if (buffer.length < length) {
    throw new Error(`buffer of ${buffer.length} bytes cannot hold a ${length}-byte partyPositions`);
  }
  assertField(tick, MAX_U32, `partyPositions tick ${tick}`);
  for (let index = 0; index < members.length; index += 1) {
    const member = members[index] as PartyPosition;
    assertField(member.place, MAX_PARTY_PLACE, `partyPositions place ${member.place}`);
    assertField(member.x, MAX_U16, `partyPositions x ${member.x}`);
    assertField(member.y, MAX_U16, `partyPositions y ${member.y}`);
    putPartyPositionEntry(buffer, index, member.place, member.x, member.y);
  }
  return putPartyPositionsHeader(buffer, tick, members.length);
}

/**
 * Decodes a `partyPositions` frame into a record. Never throws and reads no byte past the frame: a frame shorter than
 * its header, a count past 5, a length other than `8 + 5 * count`, a place past 5 or another type is malformed, and
 * another version a mismatch, each leaving the record untouched. A count of 0 is well formed: no member is on the map.
 *
 * @param frame The whole frame.
 * @param out Record to fill.
 * @returns The verdict.
 */
export function decodePartyPositions(frame: Uint8Array, out: PartyPositionsRecord): DecodeVerdict {
  if (frame.length < PARTY_POSITIONS_HEADER_BYTES) {
    return MALFORMED;
  }
  const count = frame[COUNT_OFFSET] as number;
  if (count > MAX_PARTY_POSITIONS) {
    return MALFORMED;
  }
  const header = checkHeader(frame, MessageType.PartyPositions, partyPositionsBytes(count));
  if (header.kind !== "ok") {
    return header;
  }
  for (let index = 0; index < count; index += 1) {
    if ((frame[PARTY_POSITIONS_HEADER_BYTES + PARTY_POSITION_ENTRY_BYTES * index] as number) > MAX_PARTY_PLACE) {
      return MALFORMED;
    }
  }
  for (let index = 0; index < count; index += 1) {
    const at = PARTY_POSITIONS_HEADER_BYTES + PARTY_POSITION_ENTRY_BYTES * index;
    out.places[index] = frame[at] as number;
    out.xs[index] = readU16(frame, at + 1);
    out.ys[index] = readU16(frame, at + 3);
  }
  out.tick = readU32(frame, HEADER_BYTES);
  out.count = count;
  return OK;
}
